import { catalogPage } from './catalog-config';
import { useEffect, useState, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { apiOrigin, useData, useList } from '@yingbaba/api';
import {
  Button,
  ButtonLink,
  Empty,
  ErrorNotice,
  Image,
  Pagination,
  QueryState,
  Select,
  TagList,
} from '@yingbaba/ui';
import {
  ArrowRight,
  Film,
  Music,
  Image as ImageIcon,
  Layers,
  Play,
  ShieldCheck,
  Search,
} from '@yingbaba/icons';
import { money, siteURL, digitalPreviews, digitalPreviewLabel } from '@yingbaba/utils';
import type { DigitalAssetView, DigitalPolicy } from '@yingbaba/types';
const categories = [
  ['', '全部素材'],
  ['video', '视频'],
  ['image', '图片'],
  ['audio', '音频'],
  ['template', '后期模板'],
  ['model3d', '三维素材'],
];
const categoryName = (key: string) => categories.find(([v]) => v === key)?.[1] || '数字资源';
const icons: Record<string, typeof Film> = {
  video: Film,
  audio: Music,
  image: ImageIcon,
  template: Layers,
  model3d: Layers,
};
const previewURL = (asset: string, file: string) =>
  `${apiOrigin()}/api/v1/digital/assets/${asset}/preview/${file}`;
const licenseNames: Record<string, string> = {
  personal: '个人创作',
  business: '商业项目',
  extended: '扩展项目',
};
function DigitalCard({ view }: { view: DigitalAssetView }) {
  const image = view.files.find((f) => f.kind === 'preview' && f.mime.startsWith('image/'));
  const Icon = icons[view.release.category] || Film;
  const min = view.prices.length ? Math.min(...view.prices.map((p) => p.amount_minor)) : 0;
  return (
    <Link to={`/digital-resources/${view.asset.id}`} className="digital-asset-card">
      <div className="digital-asset-cover">
        {image ? (
          <Image
            src={previewURL(view.asset.id, image.id)}
            fallbackSrc={null}
            alt={`${view.release.title}预览`}
          />
        ) : (
          <div className={`digital-asset-placeholder digital-category-${view.release.category}`}>
            <Icon size={42} />
            <span>{categoryName(view.release.category)}预览</span>
          </div>
        )}
        <span className="digital-cover-type">{categoryName(view.release.category)}</span>
        {view.files.some((f) => f.kind === 'preview' && f.mime.startsWith('video/')) && (
          <span className="digital-cover-play">
            <Play size={17} />
          </span>
        )}
      </div>
      <div className="digital-asset-copy">
        <h2>{view.release.title}</h2>
        <p>{view.release.specifications || view.release.description}</p>
        <div className="row spread">
          <span className="digital-license-count">{view.prices.length} 种授权方案</span>
          <strong>{min ? `${money(min)} 起` : '查看授权'}</strong>
        </div>
      </div>
    </Link>
  );
}
export function DigitalCatalog() {
  const [params, setParams] = useSearchParams();
  const category = params.get('category') || '',
    q = params.get('q') || '',
    sort = params.get('sort') || 'latest',
    page = catalogPage(params.get('page'));
  const [search, setSearch] = useState(q);
  useEffect(() => setSearch(q), [q]);
  const query = useList<DigitalAssetView>(
    `/digital/assets?category=${encodeURIComponent(category)}&q=${encodeURIComponent(q)}&sort=${encodeURIComponent(sort)}&page=${page}`,
  );
  function filter(key: string, value: string) {
    setParams((p) => {
      value ? p.set(key, value) : p.delete(key);
      p.delete('page');
      return p;
    });
  }
  return (
    <>
      <section className="digital-market-hero">
        <div className="container">
          <div className="digital-hero-heading">
            <div>
              <h1>为好作品，找到好素材</h1>
              <p>视频、图片、音频与制作模板，为下一次创作寻找灵感。</p>
            </div>
            <a
              className="digital-workspace-link"
              href={siteURL('work', '/digital-resources?tab=purchases')}
            >
              我的购买
              <ArrowRight size={15} />
            </a>
          </div>
          <form
            className="digital-catalog-search"
            role="search"
            aria-label="查找数字资源"
            onSubmit={(e) => {
              e.preventDefault();
              filter('q', search.trim());
            }}
          >
            <Search size={20} aria-hidden="true" />
            <input
              aria-label="搜索数字资源"
              placeholder="搜索素材、标签或制作软件"
              maxLength={200}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button type="submit">搜索</Button>
          </form>
        </div>
      </section>
      <div className="container section digital-market-body">
        <div className="digital-filter-row">
          <div role="group" aria-label="数字资源类型" className="digital-category-chips">
            {categories.map(([key, label]) => (
              <button
                key={key}
                type="button"
                aria-pressed={category === key}
                className={category === key ? 'active' : ''}
                onClick={() => filter('category', key)}
              >
                {label}
              </button>
            ))}
          </div>
          <Select aria-label="数字资源排序" value={sort} onChange={(v) => filter('sort', v)}>
            <option value="latest">最新发布</option>
            <option value="oldest">最早发布</option>
          </Select>
        </div>
        <div className="digital-catalog-results">
          <QueryState query={query}>
            {query.data?.items.length ? (
              <div className="digital-market-grid">
                {query.data.items.map((v) => (
                  <DigitalCard key={v.asset.id} view={v} />
                ))}
              </div>
            ) : (
              <Empty
                title={q || category ? '没有找到相关素材' : '素材库还没有上架内容'}
                description={
                  q || category
                    ? '试试其他关键词，或切换素材类型。'
                    : '欢迎上传自己的创作作品，让更多人发现并使用。'
                }
                action={
                  q || category ? (
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setSearch('');
                        setParams({});
                      }}
                    >
                      浏览全部素材
                    </Button>
                  ) : (
                    <ButtonLink
                      to={siteURL('work', '/digital-resources?tab=supplier')}
                      variant="secondary"
                    >
                      发布我的素材
                    </ButtonLink>
                  )
                }
              />
            )}
          </QueryState>
        </div>
        <Pagination
          page={page}
          size={query.data?.page_size || 18}
          total={query.data?.total || 0}
          onPage={(p) =>
            setParams((prev) => {
              prev.set('page', String(p));
              return prev;
            })
          }
        />
        <aside className="digital-catalog-help" aria-label="素材授权说明">
          <ShieldCheck size={19} aria-hidden="true" />
          <p>按实际用途选择授权，购买后可在「我的购买」中下载素材与授权凭证。</p>
          <Link to="/help?category=resources#digital-resources">
            授权与下载说明
            <ArrowRight size={14} />
          </Link>
        </aside>
      </div>
    </>
  );
}
export function DigitalDetail() {
  const { id } = useParams();
  const query = useData<DigitalAssetView>(`/digital/assets/${id}`);
  const policy = useData<DigitalPolicy>('/digital/policy');
  const [selected, setSelected] = useState(''),
    [fileID, setFileID] = useState('');
  const media = useRef<HTMLMediaElement | null>(null);
  const [mediaError, setMediaError] = useState(false);
  const [previewAttempt, setPreviewAttempt] = useState(0);
  const v = query.data;
  const previews = v ? digitalPreviews(v.files, v.release.category) : [];
  const file = previews.find((f) => f.id === fileID) || previews[0];
  const cover = previews.find((f) => f.mime.startsWith('image/'));
  useEffect(() => {
    setFileID('');
    setSelected('');
  }, [id]);
  useEffect(() => {
    setMediaError(false);
    setPreviewAttempt(0);
  }, [id, file?.id]);
  // Metadata can fail before React attaches events to server-rendered media.
  useEffect(() => {
    if (media.current?.error) setMediaError(true);
  }, [id, file?.id, previewAttempt]);
  const offer =
    v?.prices.find((p) => p.code === selected) ||
    v?.prices.find((p) => p.code === 'business') ||
    v?.prices[0];
  useEffect(() => {
    if (v) {
      document.title = `${v.release.title} · 影爸爸`;
      document
        .querySelector('meta[name="description"]')
        ?.setAttribute('content', v.release.description.slice(0, 160));
    }
  }, [v]);
  return (
    <div className="container section">
      <nav className="breadcrumb" aria-label="当前位置">
        <Link to="/">首页</Link>
        <span aria-hidden="true">/</span>
        <Link to="/digital-resources">数字资源</Link>
        <span aria-hidden="true">/</span>
        <span>{v?.release.title || '资源详情'}</span>
      </nav>
      <QueryState query={query}>
        {v && (
          <>
            <div className="digital-detail-layout">
              <section className="digital-preview-panel">
                <div className="digital-preview-stage">
                  {mediaError ? (
                    <div className="digital-preview-error" role="status">
                      <p>这个预览暂时无法加载</p>
                      <p className="small muted">请检查网络后重试，也可以切换其他预览。</p>
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setMediaError(false);
                          setPreviewAttempt((value) => value + 1);
                        }}
                      >
                        重新加载预览
                      </Button>
                    </div>
                  ) : file?.mime.startsWith('image/') ? (
                    <img
                      key={`${file.id}:${previewAttempt}`}
                      src={previewURL(v.asset.id, file.id)}
                      alt={`${v.release.title}预览`}
                      onError={() => setMediaError(true)}
                    />
                  ) : file?.mime.startsWith('video/') ? (
                    <video
                      ref={(node) => {
                        media.current = node;
                      }}
                      key={`${file.id}:${previewAttempt}`}
                      src={previewURL(v.asset.id, file.id)}
                      poster={cover ? previewURL(v.asset.id, cover.id) : undefined}
                      aria-label={`${v.release.title}视频预览`}
                      onError={() => setMediaError(true)}
                      controls
                      preload="metadata"
                      playsInline
                    />
                  ) : file?.mime.startsWith('audio/') ? (
                    <div className="digital-audio-preview">
                      {cover ? (
                        <Image
                          src={previewURL(v.asset.id, cover.id)}
                          alt={`${v.release.title}封面`}
                          fallbackSrc={null}
                        />
                      ) : (
                        <Music size={48} />
                      )}
                      <audio
                        ref={(node) => {
                          media.current = node;
                        }}
                        key={`${file.id}:${previewAttempt}`}
                        src={previewURL(v.asset.id, file.id)}
                        aria-label={`${v.release.title}音频预览`}
                        onError={() => setMediaError(true)}
                        controls
                        preload="metadata"
                      />
                    </div>
                  ) : (
                    <p>预览正在整理中</p>
                  )}
                </div>
                {previews.length > 1 && (
                  <div className="digital-preview-options" role="group" aria-label="预览文件">
                    {previews.map((f, i) => (
                      <button
                        key={f.id}
                        className={f.id === file?.id ? 'active' : ''}
                        aria-pressed={f.id === file?.id}
                        title={f.name}
                        aria-label={`${digitalPreviewLabel(f.mime)}预览 ${i + 1}`}
                        onClick={() => setFileID(f.id)}
                      >
                        {f.mime.startsWith('image/') ? (
                          <Image src={previewURL(v.asset.id, f.id)} alt="" fallbackSrc={null} />
                        ) : f.mime.startsWith('video/') ? (
                          <Play size={20} />
                        ) : (
                          <Music size={20} />
                        )}
                        <span>
                          {digitalPreviewLabel(f.mime)} {i + 1}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                <p className="small muted digital-preview-note">
                  当前展示为预览内容，资源格式与使用范围请查看下方说明。
                </p>
              </section>
              <aside className="panel digital-license-panel">
                <span className="eyebrow">{categoryName(v.release.category)}</span>
                <h1>{v.release.title}</h1>
                <TagList value={v.release.tags} />
                <fieldset className="digital-license-options">
                  <legend>选择授权用途</legend>
                  {v.prices.map((p) => (
                    <label key={p.code} className={offer?.code === p.code ? 'selected' : ''}>
                      <input
                        type="radio"
                        name="license"
                        value={p.code}
                        checked={offer?.code === p.code}
                        onChange={() => setSelected(p.code)}
                      />
                      <span>
                        <strong>{licenseNames[p.code] || p.code}授权</strong>
                        <small>
                          {policy.data?.licenses.find((l) => l.code === p.code)?.scope ||
                            '查看下方授权说明'}
                        </small>
                      </span>
                      <b>{money(p.amount_minor)}</b>
                    </label>
                  ))}
                </fieldset>
                {offer && (
                  <p className="small muted">
                    {policy.data?.licenses.find((l) => l.code === offer.code)?.restrictions}
                  </p>
                )}
                {v.can_buy && offer ? (
                  <ButtonLink
                    to={siteURL(
                      'work',
                      `/digital-resources?asset=${v.asset.id}&license=${offer.code}`,
                    )}
                    className="btn-wide"
                  >
                    购买此授权
                    <ArrowRight size={15} />
                  </ButtonLink>
                ) : (
                  <p className="digital-availability-note">
                    当前可浏览预览，购买开放后可在这里选择授权。
                  </p>
                )}
                <a className="link" href={siteURL('work', '/digital-resources')}>
                  我的购买与授权记录
                  <ArrowRight size={14} />
                </a>
              </aside>
            </div>
            <div className="digital-detail-info">
              <section className="panel">
                <h2>资源说明</h2>
                <p className="prose">{v.release.description}</p>
                {v.release.specifications && (
                  <>
                    <h3>文件规格</h3>
                    <p className="prose">{v.release.specifications}</p>
                  </>
                )}
                {v.release.software && (
                  <>
                    <h3>软件与依赖</h3>
                    <p className="prose">{v.release.software}</p>
                  </>
                )}
                {v.rights.ai_generated && (
                  <>
                    <h3>AI 创作说明</h3>
                    <p className="prose">{v.rights.ai_provenance}</p>
                  </>
                )}
              </section>
              <section className="panel">
                <h2>权利与授权范围</h2>
                <dl className="brief-summary-facts">
                  <div>
                    <dt>权利人</dt>
                    <dd>{v.rights.holder_name}</dd>
                  </div>
                  <div>
                    <dt>授权地区</dt>
                    <dd>{v.rights.territory === 'CN' ? '中国大陆' : '全球'}</dd>
                  </div>
                  {[
                    ['第三方内容', v.rights.third_party_clearance],
                    ['肖像', v.rights.likeness_clearance],
                    ['音乐', v.rights.music_clearance],
                  ].map(([label, status]) => (
                    <div key={label}>
                      <dt>{label}</dt>
                      <dd>
                        {status === 'none'
                          ? '不涉及'
                          : status === 'cleared'
                            ? '供稿人已提交授权声明'
                            : '待核对'}
                      </dd>
                    </div>
                  ))}
                </dl>
                <p className="small muted">
                  素材中包含的音乐、字体、肖像等，应按本次选择的许可和实际使用范围核对；单独提取、转售或训练
                  AI 不在普通项目授权范围内。
                </p>
                <Link className="link" to="/help?category=resources#digital-resources">
                  了解购买、授权与下载
                  <ArrowRight size={14} />
                </Link>
              </section>
            </div>
          </>
        )}
      </QueryState>
    </div>
  );
}
